import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Building2, Plus, Ban, Pencil, Check, Copy, Trash2, Play,
  MessageCircle, MessageCircleOff, Send, Palette,
} from "lucide-react";
import { api, type Invite, type Site, type Tenant } from "../api";
import { ConfirmModal, PromptModal, type PromptField } from "../components/Modal";
import { TenantThemeModal } from "../components/TenantThemeModal";
import { Alert, PageHeader } from "../components/Page";

/** Best-effort human-name → slug converter. Lower-cased, dashes for spaces,
 *  strips non [a-z0-9-]. Leaves the user free to edit further in the field. */
function toSlug(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 100);
}

function isEmailShaped(v: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
}

export function SuperAdmin() {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [latestInvite, setLatestInvite] = useState<Invite | null>(null);

  // Create-tenant form state
  const [newName, setNewName] = useState("");
  const [newSlug, setNewSlug] = useState("");
  const [slugEditedManually, setSlugEditedManually] = useState(false);
  const [newQuota, setNewQuota] = useState<string>("1000");
  const [creating, setCreating] = useState(false);
  const [createErrors, setCreateErrors] = useState<{ name?: string; slug?: string; quota?: string }>({});

  // Per-tenant invite email state + per-tenant "busy" markers so each row
  // shows its own spinner instead of a global one.
  const [inviteEmailFor, setInviteEmailFor] = useState<Record<string, string>>({});
  const [busyRow, setBusyRow] = useState<Record<string, string | null>>({});
  const [openSitesFor, setOpenSitesFor] = useState<string | null>(null);

  // Modal state — one at a time is fine here.
  const [confirmSuspend, setConfirmSuspend] = useState<Tenant | null>(null);
  const [confirmUnsuspend, setConfirmUnsuspend] = useState<Tenant | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Tenant | null>(null);
  const [editQuotaFor, setEditQuotaFor] = useState<Tenant | null>(null);
  const [renameFor, setRenameFor] = useState<Tenant | null>(null);
  const [themeFor, setThemeFor] = useState<Tenant | null>(null);

  const refresh = useCallback(async () => {
    try {
      const [tList, sList] = await Promise.all([api.listTenants(), api.listAllSites()]);
      setTenants(tList);
      setSites(sList);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  // Auto-derive slug from name until the user manually edits the slug field.
  useEffect(() => {
    if (!slugEditedManually) setNewSlug(toSlug(newName));
  }, [newName, slugEditedManually]);

  // Toast auto-clear.
  useEffect(() => {
    if (!success) return;
    const t = window.setTimeout(() => setSuccess(null), 3500);
    return () => window.clearTimeout(t);
  }, [success]);

  function setRowBusy(tenantId: string, label: string | null) {
    setBusyRow((s) => ({ ...s, [tenantId]: label }));
  }

  async function withErr<T>(fn: () => Promise<T>, opts: { onOk?: string } = {}): Promise<T | null> {
    try {
      const r = await fn();
      if (opts.onOk) setSuccess(opts.onOk);
      return r;
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      return null;
    }
  }

  /* ─── Create-tenant form ─── */

  function validateCreate(): boolean {
    const errs: typeof createErrors = {};
    if (newName.trim().length < 2) errs.name = "Enter a tenant name (2+ characters).";
    if (!/^[a-z0-9-]{2,100}$/.test(newSlug)) errs.slug = "Slug must be lowercase letters, digits, or dashes (2–100 chars).";
    if (newQuota !== "" && (!/^\d+$/.test(newQuota) || Number(newQuota) < 0)) {
      errs.quota = "Quota must be a non-negative whole number, or blank for unlimited.";
    }
    setCreateErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function createTenant(e: React.FormEvent) {
    e.preventDefault();
    if (!validateCreate()) return;
    setCreating(true); setError(null);
    const quota = newQuota === "" ? null : Number(newQuota);
    const r = await withErr(() => api.createTenant(newName.trim(), newSlug, quota), {
      onOk: `Tenant "${newName.trim()}" created.`,
    });
    if (r) {
      setNewName(""); setNewSlug(""); setNewQuota("1000"); setSlugEditedManually(false);
      setCreateErrors({});
      await refresh();
    }
    setCreating(false);
  }

  /* ─── Invite ─── */

  async function sendInvite(t: Tenant) {
    const email = (inviteEmailFor[t.id] || "").trim();
    if (!isEmailShaped(email)) {
      setError(`Enter a valid email address for ${t.name} before sending an invite.`);
      return;
    }
    setError(null);
    setRowBusy(t.id, "inviting");
    const inv = await withErr(() => api.createInvite(email, t.id, "owner"));
    if (inv) {
      setLatestInvite(inv);
      setInviteEmailFor((s) => ({ ...s, [t.id]: "" }));
      setSuccess(inv.email_sent
        ? `Invitation emailed to ${email}.`
        : `Invitation created — email failed, share the link manually.`);
    }
    setRowBusy(t.id, null);
  }

  /* ─── QA toggle ─── */

  async function toggleTenantQa(t: Tenant) {
    setRowBusy(t.id, "qa");
    await withErr(() => api.setTenantQa(t.id, !t.qa_enabled));
    await refresh();
    setRowBusy(t.id, null);
  }
  async function setSiteQa(siteId: string, enabled: boolean | null) {
    await withErr(() => api.setSiteQa(siteId, enabled));
    await refresh();
  }

  /* ─── Modal handlers ─── */

  async function submitEditQuota(values: Record<string, string>) {
    if (!editQuotaFor) return;
    const raw = values.quota.trim();
    const quota = raw === "" ? null : Number(raw);
    await api.updateQuota(editQuotaFor.id, quota);
    setSuccess(`Quota updated for ${editQuotaFor.name}.`);
    setEditQuotaFor(null);
    await refresh();
  }
  async function submitRename(values: Record<string, string>) {
    if (!renameFor) return;
    await api.renameTenant(renameFor.id, values.name.trim(), values.slug, renameFor.page_quota);
    setSuccess(`Tenant renamed to "${values.name.trim()}".`);
    setRenameFor(null);
    await refresh();
  }
  async function doSuspend() {
    if (!confirmSuspend) return;
    const t = confirmSuspend;
    setConfirmSuspend(null);
    setRowBusy(t.id, "suspend");
    await withErr(() => api.suspendTenant(t.id), { onOk: `${t.name} suspended.` });
    await refresh();
    setRowBusy(t.id, null);
  }
  async function doUnsuspend() {
    if (!confirmUnsuspend) return;
    const t = confirmUnsuspend;
    setConfirmUnsuspend(null);
    setRowBusy(t.id, "unsuspend");
    await withErr(() => api.unsuspendTenant(t.id), { onOk: `${t.name} un-suspended.` });
    await refresh();
    setRowBusy(t.id, null);
  }
  async function doDelete() {
    if (!confirmDelete) return;
    const t = confirmDelete;
    setConfirmDelete(null);
    setRowBusy(t.id, "delete");
    await withErr(() => api.deleteTenant(t.id), { onOk: `${t.name} deleted.` });
    await refresh();
    setRowBusy(t.id, null);
  }

  return (
    <div>
      <PageHeader title="Super Admin" description="Manage all tenants, page quotas, and onboarding invites." />

      {error && <Alert onDismiss={() => setError(null)}>{error}</Alert>}
      {success && <Alert tone="success" onDismiss={() => setSuccess(null)}>{success}</Alert>}

      {latestInvite && <InviteBanner invite={latestInvite} onDismiss={() => setLatestInvite(null)} />}

      {/* Create tenant */}
      <form onSubmit={createTenant} className="card mb-6" noValidate>
        <div className="card-header">
          <h2 className="text-base font-semibold text-ink flex items-center gap-2">
            <Plus className="h-4 w-4 text-brand-500" /> Create tenant
          </h2>
        </div>
        <div className="card-body grid grid-cols-1 md:grid-cols-12 gap-3 items-start">
          <div className="md:col-span-4">
            <label htmlFor="ct-name" className="block text-sm font-medium text-slate-700 mb-1">
              Name <span className="text-rose-600" aria-hidden="true">*</span>
            </label>
            <input
              id="ct-name"
              className={`input ${createErrors.name ? "!border-rose-400" : ""}`}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Lancaster Airport"
              aria-invalid={!!createErrors.name}
              aria-describedby={createErrors.name ? "ct-name-err" : undefined}
              required
            />
            {createErrors.name && <p id="ct-name-err" className="mt-1 text-xs text-rose-700">{createErrors.name}</p>}
          </div>
          <div className="md:col-span-4">
            <label htmlFor="ct-slug" className="block text-sm font-medium text-slate-700 mb-1">
              Slug <span className="text-rose-600" aria-hidden="true">*</span>
            </label>
            <input
              id="ct-slug"
              className={`input font-mono text-sm ${createErrors.slug ? "!border-rose-400" : ""}`}
              value={newSlug}
              onChange={(e) => {
                setSlugEditedManually(true);
                setNewSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"));
              }}
              placeholder="lancaster-airport"
              aria-invalid={!!createErrors.slug}
              aria-describedby={createErrors.slug ? "ct-slug-err" : "ct-slug-help"}
              pattern="[a-z0-9-]+"
              required
            />
            {createErrors.slug
              ? <p id="ct-slug-err" className="mt-1 text-xs text-rose-700">{createErrors.slug}</p>
              : <p id="ct-slug-help" className="mt-1 text-xs text-slate-500">Auto-generated from name — you can edit it.</p>}
          </div>
          <div className="md:col-span-2">
            <label htmlFor="ct-quota" className="block text-sm font-medium text-slate-700 mb-1">Page quota</label>
            <input
              id="ct-quota"
              className={`input ${createErrors.quota ? "!border-rose-400" : ""}`}
              type="number"
              min={0}
              value={newQuota}
              onChange={(e) => setNewQuota(e.target.value)}
              placeholder="Blank = ∞"
              aria-invalid={!!createErrors.quota}
              aria-describedby={createErrors.quota ? "ct-quota-err" : undefined}
            />
            {createErrors.quota && <p id="ct-quota-err" className="mt-1 text-xs text-rose-700">{createErrors.quota}</p>}
          </div>
          <div className="md:col-span-2 flex md:pt-6">
            <button type="submit" className="btn-primary w-full" disabled={creating}>
              {creating ? (<><span className="spinner" aria-hidden="true" /> Creating…</>) : "Create tenant"}
            </button>
          </div>
        </div>
      </form>

      {/* Tenant list */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-base font-semibold text-ink flex items-center gap-2">
            <Building2 className="h-4 w-4 text-brand-500" /> Tenants ({tenants.length})
          </h2>
        </div>
        {loading ? (
          <div className="card-body text-sm text-slate-500 flex items-center gap-2">
            <span className="spinner text-brand-500" aria-hidden="true" /> Loading tenants…
          </div>
        ) : tenants.length === 0 ? (
          <div className="card-body text-sm text-slate-500">No tenants yet. Create one above to get started.</div>
        ) : (
          <ul className="divide-y divide-slate-200">
            {tenants.map((t) => {
              const tenantSites = sites.filter((s) => s.tenant_id === t.id);
              const expanded = openSitesFor === t.id;
              const rowBusy = busyRow[t.id] || null;
              const rowInviteEmail = inviteEmailFor[t.id] || "";
              const inviteInvalid = rowInviteEmail.length > 0 && !isEmailShaped(rowInviteEmail);
              return (
                <li key={t.id} className="px-5 py-4">
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-center">
                    <div className="md:col-span-4">
                      <div className="font-medium text-ink">{t.name}</div>
                      <div className="text-xs text-slate-500">Slug: <code className="font-mono">{t.slug}</code></div>
                    </div>
                    <div className="md:col-span-3 text-sm text-slate-700">
                      Pages: <strong>{t.pages_used}</strong> / {t.page_quota ?? "∞"}
                      {t.is_suspended && <span className="ml-2 pill bg-rose-100 text-rose-800">suspended</span>}
                    </div>
                    <div className="md:col-span-3 flex gap-2">
                      <input
                        className={`input ${inviteInvalid ? "!border-rose-400" : ""}`}
                        placeholder="owner@example.com"
                        type="email"
                        value={rowInviteEmail}
                        onChange={(e) => setInviteEmailFor((s) => ({ ...s, [t.id]: e.target.value }))}
                        aria-label={`Invite email for ${t.name}`}
                        disabled={rowBusy === "inviting"}
                      />
                      <button
                        onClick={() => sendInvite(t)}
                        className="btn-secondary"
                        disabled={rowBusy === "inviting" || rowInviteEmail.length === 0 || inviteInvalid}
                        title="Send invitation email"
                      >
                        {rowBusy === "inviting"
                          ? <><span className="spinner" aria-hidden="true" /> Sending</>
                          : <><Send className="h-4 w-4" /> Invite</>}
                      </button>
                    </div>
                    <div className="md:col-span-2 flex gap-1 justify-end">
                      <button onClick={() => setEditQuotaFor(t)} className="btn-ghost" title="Edit page quota" aria-label={`Edit quota for ${t.name}`}>
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button onClick={() => setRenameFor(t)} className="btn-ghost" title="Rename tenant" aria-label={`Rename ${t.name}`}>Aa</button>
                      <button onClick={() => setThemeFor(t)} className="btn-ghost" title="Edit reader theme" aria-label={`Edit reader theme for ${t.name}`}>
                        <Palette className="h-4 w-4" />
                      </button>
                      {t.is_suspended ? (
                        <button
                          onClick={() => setConfirmUnsuspend(t)}
                          className="btn-ghost text-emerald-600"
                          title="Un-suspend tenant"
                          aria-label={`Un-suspend ${t.name}`}
                          disabled={rowBusy === "unsuspend"}
                        >
                          {rowBusy === "unsuspend"
                            ? <span className="spinner" aria-hidden="true" />
                            : <Play className="h-4 w-4" />}
                        </button>
                      ) : (
                        <button
                          onClick={() => setConfirmSuspend(t)}
                          className="btn-ghost text-amber-600"
                          title="Suspend tenant"
                          aria-label={`Suspend ${t.name}`}
                          disabled={rowBusy === "suspend"}
                        >
                          {rowBusy === "suspend"
                            ? <span className="spinner" aria-hidden="true" />
                            : <Ban className="h-4 w-4" />}
                        </button>
                      )}
                      <button
                        onClick={() => setConfirmDelete(t)}
                        className="btn-ghost text-rose-600"
                        title="Delete tenant"
                        aria-label={`Delete ${t.name}`}
                        disabled={rowBusy === "delete"}
                      >
                        {rowBusy === "delete"
                          ? <span className="spinner" aria-hidden="true" />
                          : <Trash2 className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-slate-100">
                    <div className="flex items-center gap-2">
                      {t.qa_enabled
                        ? <MessageCircle className="h-4 w-4 text-emerald-600" aria-hidden="true" />
                        : <MessageCircleOff className="h-4 w-4 text-slate-400" aria-hidden="true" />}
                      <span className="text-xs text-slate-600">Ask-a-Question default</span>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={t.qa_enabled}
                        onClick={() => toggleTenantQa(t)}
                        disabled={rowBusy === "qa"}
                        className={
                          "relative inline-flex h-5 w-9 items-center rounded-full transition-colors disabled:opacity-60 " +
                          (t.qa_enabled ? "bg-emerald-500" : "bg-slate-300")
                        }
                        title={t.qa_enabled ? "Click to disable for this tenant" : "Click to enable for this tenant"}
                      >
                        <span
                          className={
                            "inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform " +
                            (t.qa_enabled ? "translate-x-4" : "translate-x-0.5")
                          }
                        />
                      </button>
                      <span className={"text-xs font-medium " + (t.qa_enabled ? "text-emerald-700" : "text-slate-500")}>
                        {t.qa_enabled ? "ON" : "OFF"}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="text-xs text-brand-600 hover:text-brand-700 hover:underline"
                      onClick={() => setOpenSitesFor(expanded ? null : t.id)}
                    >
                      {expanded ? "Hide site overrides" : `Per-site overrides${tenantSites.length ? ` (${tenantSites.length})` : ""}`}
                    </button>
                  </div>
                  {expanded && (
                    <SiteQaOverrides
                      sites={tenantSites}
                      tenantDefault={t.qa_enabled}
                      onChange={setSiteQa}
                    />
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* ─── Modals ─── */}

      <ConfirmModal
        open={!!confirmSuspend}
        onClose={() => setConfirmSuspend(null)}
        onConfirm={doSuspend}
        title={`Suspend ${confirmSuspend?.name}?`}
        message={<>Users in <strong>{confirmSuspend?.name}</strong> will be blocked from uploading and converting. You can un-suspend at any time.</>}
        confirmLabel="Suspend tenant"
        tone="warning"
      />
      <ConfirmModal
        open={!!confirmUnsuspend}
        onClose={() => setConfirmUnsuspend(null)}
        onConfirm={doUnsuspend}
        title={`Un-suspend ${confirmUnsuspend?.name}?`}
        message={<>Users of <strong>{confirmUnsuspend?.name}</strong> will regain access immediately.</>}
        confirmLabel="Un-suspend"
      />
      <ConfirmModal
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={doDelete}
        title={`Delete ${confirmDelete?.name}?`}
        message={<>Removes <strong>{confirmDelete?.name}</strong> along with its users, sites, and documents. Users of this workspace will no longer be able to sign in.</>}
        confirmLabel="Delete tenant"
        tone="danger"
      />
      <PromptModal
        open={!!editQuotaFor}
        onClose={() => setEditQuotaFor(null)}
        onSubmit={submitEditQuota}
        title={`Edit page quota — ${editQuotaFor?.name ?? ""}`}
        description="Set the monthly conversion page ceiling. Leave blank for unlimited."
        fields={useMemo<PromptField[]>(() => [{
          name: "quota",
          label: "Page quota",
          type: "number",
          initialValue: editQuotaFor?.page_quota == null ? "" : String(editQuotaFor.page_quota),
          helpText: "Blank = unlimited",
          validate: (v) => {
            if (v.trim() === "") return null;
            if (!/^\d+$/.test(v.trim())) return "Enter a non-negative whole number.";
            return null;
          },
        }], [editQuotaFor])}
        submitLabel="Save quota"
      />
      <PromptModal
        open={!!renameFor}
        onClose={() => setRenameFor(null)}
        onSubmit={submitRename}
        title={`Rename ${renameFor?.name ?? ""}`}
        description="Editing the slug can break existing links — only change it if you know downstream tooling can cope."
        fields={useMemo<PromptField[]>(() => [
          {
            name: "name",
            label: "Tenant name",
            initialValue: renameFor?.name || "",
            required: true,
            minLength: 2,
          },
          {
            name: "slug",
            label: "Slug",
            initialValue: renameFor?.slug || "",
            required: true,
            pattern: "[a-z0-9-]{2,100}",
            helpText: "Lowercase letters, digits, and dashes only.",
            transform: (v) => v.toLowerCase().replace(/[^a-z0-9-]/g, "-"),
          },
        ], [renameFor])}
        submitLabel="Save"
      />
      <TenantThemeModal
        tenant={themeFor}
        onClose={() => setThemeFor(null)}
        onSaved={(updated) => {
          setTenants((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
          setSuccess(`Reader theme updated for ${updated.name}.`);
        }}
      />
    </div>
  );
}

function SiteQaOverrides({
  sites,
  tenantDefault,
  onChange,
}: {
  sites: Site[];
  tenantDefault: boolean;
  onChange: (siteId: string, enabled: boolean | null) => void;
}) {
  if (sites.length === 0) {
    return <div className="mt-3 text-xs text-slate-400 italic">No sites yet for this tenant.</div>;
  }
  return (
    <div className="mt-3 rounded border border-slate-200 bg-slate-50 divide-y divide-slate-200">
      {sites.map((s) => {
        const inherit = s.qa_enabled === null || s.qa_enabled === undefined;
        const effective = inherit ? tenantDefault : !!s.qa_enabled;
        return (
          <div key={s.id} className="px-3 py-2 flex items-center justify-between text-sm">
            <div className="min-w-0 flex-1 truncate">
              <span className="font-medium text-ink">{s.domain}</span>
              <span className="ml-2 text-xs text-slate-500">
                effective: <strong className={effective ? "text-emerald-700" : "text-slate-500"}>
                  {effective ? "enabled" : "disabled"}
                </strong>
              </span>
            </div>
            <div className="flex items-center gap-1">
              <select
                className="input py-1 text-xs"
                value={inherit ? "inherit" : (s.qa_enabled ? "on" : "off")}
                onChange={(e) => {
                  const v = e.target.value;
                  onChange(s.id, v === "inherit" ? null : v === "on");
                }}
                title="Override the tenant default for this site"
              >
                <option value="inherit">Inherit ({tenantDefault ? "on" : "off"})</option>
                <option value="on">Force ON</option>
                <option value="off">Force OFF</option>
              </select>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function InviteBanner({ invite, onDismiss }: { invite: Invite; onDismiss: () => void }) {
  const [copied, setCopied] = useState(false);
  function copy() {
    navigator.clipboard.writeText(invite.accept_url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }
  const failed = invite.email_sent === false;
  return (
    <div className={"card mb-6 " + (failed ? "border-amber-200 bg-amber-50" : "border-brand-200 bg-brand-50")}>
      <div className="card-body">
        <div className="flex items-start justify-between gap-4">
          <div className="text-sm">
            <div className={"font-semibold " + (failed ? "text-amber-900" : "text-brand-900")}>
              Invite created for {invite.email}
            </div>
            {invite.email_sent === true && (
              <div className="text-emerald-700 mt-1">Invitation email sent. You can also share the link below directly.</div>
            )}
            {failed && (
              <div className="text-amber-900 mt-1">
                <strong>The invitation email couldn't be sent.</strong>{" "}
                {invite.email_error ? `Reason: ${invite.email_error}. ` : ""}
                Copy the link below and share it with them another way.
              </div>
            )}
            {invite.email_sent == null && (
              <div className="text-brand-800 mt-1">Share the link below.</div>
            )}
            <div className="font-mono text-xs bg-white border border-slate-200 rounded p-2 mt-2 break-all">{invite.accept_url}</div>
          </div>
          <div className="flex flex-col gap-2 shrink-0">
            <button onClick={copy} className="btn-secondary">
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              {copied ? "Copied" : "Copy link"}
            </button>
            <button onClick={onDismiss} className="btn-ghost">Dismiss</button>
          </div>
        </div>
      </div>
    </div>
  );
}
